<template>
  <div class="sms-settings">
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0">
          <MessageSquare :size="20" class="me-2" />
          Elevate SMS Configuration
        </h5>
      </div>
      <div class="card-body">
        <div v-if="loadState.IsLoading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-2"></i> Loading settings...
        </div>
        <form v-else @submit.prevent="saveSettings">
          <div class="alert alert-info mb-4">
            <i class="fas fa-info-circle me-2"></i>
            Configure your Intermedia Elevate API credentials to enable SMS messaging.
            You'll need a Service Account from your 
            <a href="https://support.intermedia.com/app/articles/detail/a_id/23712" target="_blank">
              HostPilot Control Panel
            </a>.
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Client ID</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="settings.clientId"
                placeholder="Enter Elevate Client ID"
              />
              <small class="text-muted">From your Intermedia Service Account</small>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Client Secret</label>
              <input 
                type="password" 
                class="form-control" 
                v-model="settings.clientSecret"
                placeholder="••••••••••••••••"
              />
              <small class="text-muted">Secret key (kept secure, never sent to the browser)</small>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Auth Base URL</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="settings.authBaseUrl"
                placeholder="https://auth.intermedia.com"
              />
              <small class="text-muted">Intermedia OAuth endpoint</small>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">API Base URL</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="settings.apiBaseUrl"
                placeholder="https://api.intermedia.com"
              />
              <small class="text-muted">Intermedia Messaging API endpoint</small>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">From Number</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="settings.fromNumber"
                placeholder="+15551234567"
              />
              <small class="text-muted">Your SMS-enabled Elevate phone number</small>
            </div>
            <div class="col-md-6 mb-3 d-flex align-items-end">
              <div class="form-check mb-2">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="enableSms" 
                  v-model="settings.enabled"
                />
                <label class="form-check-label" for="enableSms">
                  <strong>Enable SMS Messaging</strong>
                </label>
                <small class="text-muted d-block">
                  Allow sending and receiving SMS through the app
                </small>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button 
              type="button" 
              class="btn btn-outline-secondary" 
              @click="testConnection" 
              :disabled="testState.IsLoading || !settings.clientId || !settings.clientSecret"
            >
              <Zap :size="16" class="me-1" />
              {{ testState.IsLoading ? 'Testing...' : 'Test Connection' }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saveState.IsLoading">
              <Save :size="16" class="me-1" />
              {{ saveState.IsLoading ? 'Saving...' : 'Save Settings' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { MessageSquare, Save, Zap } from 'lucide-vue-next';
import OptionsService from '@/services/OptionsService';
import type { ElevateSettings } from '@/services/OptionsService';
import ApiService from '@/services/ApiService';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import { useApiRequest } from '@/composables';

const { state: loadState, execute: executeLoad } = useApiRequest<ElevateSettings>('sms:load');
const { state: saveState, execute: executeSave } = useApiRequest('sms:save');
const { state: testState, execute: executeTest } = useApiRequest('sms:test');

const settings = ref<ElevateSettings>({
  clientId: '',
  clientSecret: '',
  authBaseUrl: 'https://auth.intermedia.com',
  apiBaseUrl: 'https://api.intermedia.com',
  fromNumber: '',
  enabled: false
});

const loadSettings = async () => {
  await executeLoad(() => OptionsService.getElevateSettings());
  if (loadState.value.IsError) {
    Alert.notify({ text: 'Failed to load SMS settings', type: 'error' });
    return;
  }
  if (loadState.value.Data) {
    settings.value = loadState.value.Data;
  }
};

const saveSettings = async () => {
  await executeSave(() => OptionsService.saveElevateSettings(settings.value));
  if (saveState.value.IsError) {
    Alert.notify({ text: 'Failed to save SMS settings', type: 'error' });
  } else {
    Toast.success('SMS settings saved successfully!');
  }
};

const testConnection = async () => {
  await executeTest(() => ApiService.testElevateConnection());
  if (testState.value.IsError) {
    Alert.notify({ text: 'Failed to connect to Elevate. Check your credentials.', type: 'error' });
  } else {
    Toast.success('Elevate connection successful!');
  }
};

onMounted(() => {
  loadSettings();
});
</script>
